import { supabase } from '../supabase';
import { useQuery, useMutation } from '@tanstack/react-query';
import { SupervisorShift } from '@shiftly/shared';
import { DateTime } from 'luxon';
import SupabaseShiftLoggingService from '../logging/supabaseShiftLoggingService';

export type SupervisorShiftFilter = {
  field: keyof SupervisorShift | string;
  operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'not-in' | 'is';
  value: any;
};

export type SupervisorShiftSort = {
  field: keyof SupervisorShift | string;
  direction: 'asc' | 'desc';
};

export type GetSupervisorShiftsParams = {
  filters?: SupervisorShiftFilter[];
  sort?: SupervisorShiftSort[];
  enabled?: boolean;
};

export function convertDateFields(data: any): SupervisorShift {
  return {
    ...data,
    start_date_time: DateTime.fromISO(data.start_date_time, { zone: 'UTC' }),
    end_date_time: DateTime.fromISO(data.end_date_time, { zone: 'UTC' }),
    clock_in_time: data.clock_in_time ? DateTime.fromISO(data.clock_in_time, { zone: 'UTC' }) : undefined,
    created_at: DateTime.fromISO(data.created_at, { zone: 'UTC' }),
    updated_at: DateTime.fromISO(data.updated_at, { zone: 'UTC' })
  }
}

const SupabaseSupervisorShiftService = {
  async getSupervisorShifts({
    filters = [],
    sort = []
  }: GetSupervisorShiftsParams): Promise<SupervisorShift[]> {
    try {
      let query = supabase.from('supervisor_shifts').select('*');

      // Apply filters
      filters.forEach(filter => {
        switch (filter.operator) {
          case 'eq':
            query = query.eq(filter.field, filter.value);
            break;
          case 'neq':
            query = query.neq(filter.field, filter.value);
            break;
          case 'gt':
            query = query.gt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
            break;
          case 'gte':
            query = query.gte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
            break;
          case 'lt':
            query = query.lt(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
            break;
          case 'lte':
            query = query.lte(filter.field, filter.value instanceof DateTime ? filter.value.toISO() : filter.value);
            break;
          case 'in':
            query = query.in(filter.field, filter.value);
            break;
          case 'not-in':
            query = query.not(filter.field, 'in', `(${filter.value.map((v: any) => JSON.stringify(v)).join(',')})`);
            break;
          case 'is':
            query = query.is(filter.field, filter.value);
            break;
        }
      });

      // Apply sorting with stable secondary sort
      if (sort.length > 0) {
        sort.forEach(sort_option => {
          query = query.order(sort_option.field, { ascending: sort_option.direction === 'asc' });
        });
        // Add stable secondary sort if not already sorting by id
        if (!sort.some(s => s.field === 'id')) {
          query = query.order('id', { ascending: true });
        }
      } else {
        query = query.order('start_date_time', { ascending: true }).order('id', { ascending: true });
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching supervisor shifts:', error);
        throw error;
      }

      return data.map((shift: SupervisorShift) => convertDateFields(shift));
    } catch (error) {
      console.error('Error in getSupervisorShifts:', error);
      return [];
    }
  },

  async createSupervisorShift(shift: Omit<SupervisorShift, 'id' | 'created_at' | 'updated_at'>, performed_by?: string): Promise<string> {
    try {
      const now = DateTime.utc();
      const { data, error } = await supabase
        .from('supervisor_shifts')
        .insert({
          group: shift.group,
          user_id: shift.user_id || null,
          shift_code: shift.shift_code ?? null,
          start_date_time: shift.start_date_time.toISO(),
          end_date_time: shift.end_date_time.toISO(),
          submitted: shift.submitted ?? false,
          clocked_in: shift.clocked_in ?? false,
          clock_in_time: shift.clock_in_time?.toISO(),
          created_at: now.toISO(),
          updated_at: now.toISO()
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating supervisor shift:', error);
        throw error;
      }

      // Get current user for logging
      const current_user = performed_by || (await supabase.auth.getUser()).data.user?.id;
      
      // Log the supervisor shift creation if we have a user
      if (current_user) {
        try {
          const created_shift: SupervisorShift = {
            ...shift,
            id: data.id,
            created_at: now,
            updated_at: now,
          };
          await SupabaseShiftLoggingService.logSupervisorShiftCreate(created_shift, current_user);
        } catch (logError) {
          console.error('Error logging supervisor shift creation:', logError);
          // Don't throw here - the shift was created successfully
        }
      }

      return data.id;
    } catch (error) {
      console.error('Error in createSupervisorShift:', error);
      throw error;
    }
  },

  async updateSupervisorShift(shift_id: string, data: Partial<SupervisorShift>, performed_by?: string): Promise<void> {
    try {
      // Get current user for logging
      const current_user = performed_by || (await supabase.auth.getUser()).data.user?.id;
      
      // Get the current shift data for logging
      let before_data: Partial<SupervisorShift> | undefined;
      if (current_user) {
        try {
          const { data: currentShift } = await supabase
            .from('supervisor_shifts')
            .select('*')
            .eq('id', shift_id)
            .single();
          if (currentShift) {
            before_data = convertDateFields(currentShift);
          }
        } catch (error) {
          console.error('Error fetching supervisor shift for logging:', error);
        }
      }

      const update_data: any = {
        updated_at: DateTime.utc().toISO()
      };

      // Convert camelCase fields to snake_case
      Object.entries(data).forEach(([key, value]) => {
        if (key === 'id' || key === 'created_at' || key === 'updated_at') return;

        if (value instanceof DateTime) {
          update_data[key] = value.toISO();
        } else {
          update_data[key] = value;
        }
      });

      const { error } = await supabase
        .from('supervisor_shifts')
        .update(update_data)
        .eq('id', shift_id);

      if (error) {
        console.error('Error updating supervisor shift:', error);
        throw error;
      }

      // Log the supervisor shift update if we have a user and before data
      if (current_user && before_data) {
        try {
          const afterData = { ...before_data, ...data };
          await SupabaseShiftLoggingService.logSupervisorShiftUpdate(shift_id, { before: before_data, after: afterData }, current_user);
        } catch (logError) {
          console.error('Error logging supervisor shift update:', logError);
          // Don't throw here - the shift was updated successfully
        }
      }
    } catch (error) {
      console.error('Error in updateSupervisorShift:', error);
      throw error;
    }
  },

  async deleteSupervisorShift(shift_id: string, performed_by?: string): Promise<void> {
    try {
      // Get current user for logging
      const current_user = performed_by || (await supabase.auth.getUser()).data.user?.id;
      
      // Get the current shift data for logging
      let shift_data: SupervisorShift | undefined;
      if (current_user) {
        try {
          const { data: currentShift } = await supabase
            .from('supervisor_shifts')
            .select('*')
            .eq('id', shift_id)
            .single();
          if (currentShift) {
            shift_data = convertDateFields(currentShift);
          }
        } catch (error) {
          console.error('Error fetching supervisor shift for logging:', error);
        }
      }

      const { error } = await supabase
        .from('supervisor_shifts')
        .delete()
        .eq('id', shift_id);

      if (error) {
        console.error('Error deleting supervisor shift:', error);
        throw error;
      }

      // Log the supervisor shift deletion if we have a user and shift data
      if (current_user && shift_data) {
        try {
          await SupabaseShiftLoggingService.logSupervisorShiftDelete(shift_data, current_user);
        } catch (logError) {
          console.error('Error logging supervisor shift deletion:', logError);
        }
      }
    } catch (error) {
      console.error('Error in deleteSupervisorShift:', error);
      throw error;
    }
  },

  async getSupervisorShiftsForMonth(month: DateTime, group?: string): Promise<SupervisorShift[]> {
    try {
      const month_start = month.startOf('month');
      const month_end = month.endOf('month');

      const filters: SupervisorShiftFilter[] = [
        { field: 'start_date_time', operator: 'gte', value: month_start.toISO() },
        { field: 'start_date_time', operator: 'lte', value: month_end.toISO() }
      ];

      if (group) {
        filters.push({ field: 'group', operator: 'eq', value: group });
      }

      return this.getSupervisorShifts({ filters });
    } catch (error) {
      console.error('Error in getSupervisorShiftsForMonth:', error);
      return [];
    }
  },

  async deleteSupervisorShiftsByGroup(group: string, month?: DateTime): Promise<void> {
    try {
      let shifts: SupervisorShift[] = [];

      if (month) {
        shifts = await this.getSupervisorShiftsForMonth(month, group);
      } else {
        const now = DateTime.utc();
        shifts = await this.getSupervisorShiftsForMonth(now, group);
      }
      
      // Delete all shifts
      const delete_promises = shifts.map(shift => 
        this.deleteSupervisorShift(shift.id)
      );
      
      await Promise.all(delete_promises);
    } catch (error) {
      console.error('Error in deleteSupervisorShiftsByGroup:', error);
      throw error;
    }
  }
};

// React Query Hooks
export const useSupervisorShifts = (params: GetSupervisorShiftsParams = {}) => {
  return useQuery({
    queryKey: ['supabase-supervisor-shifts', params],
    queryFn: () => SupabaseSupervisorShiftService.getSupervisorShifts(params),
    enabled: params.enabled !== false
  });
};

export const useCreateSupervisorShift = () => {
  return useMutation({
    mutationFn: ({ shift, performed_by }: { shift: Omit<SupervisorShift, 'id' | 'created_at' | 'updated_at'>; performed_by?: string }) => 
      SupabaseSupervisorShiftService.createSupervisorShift(shift, performed_by),
  });
};

export const useDeleteSupervisorShift = () => {
  return useMutation({
    mutationFn: ({ shift_id, performed_by }: { shift_id: string; performed_by?: string }) => 
      SupabaseSupervisorShiftService.deleteSupervisorShift(shift_id, performed_by),
  });
};

export const useUpdateSupervisorShift = () => {
  return useMutation({
    mutationFn: ({ shift_id, data, performed_by }: { shift_id: string; data: Partial<SupervisorShift>; performed_by?: string }) => 
      SupabaseSupervisorShiftService.updateSupervisorShift(shift_id, data, performed_by),
  });
};

export const useUserSupervisorShifts = (user_id: string | undefined, start_date?: DateTime, end_date?: DateTime) => {
  const filters: SupervisorShiftFilter[] = [
    { field: 'user_id', operator: 'eq', value: user_id }
  ];

  if (start_date) {
    filters.push({ field: 'start_date_time', operator: 'gte', value: start_date.toISO() });
  }
  if (end_date) {
    filters.push({ field: 'start_date_time', operator: 'lte', value: end_date.toISO() });
  }

  return useQuery({
    queryKey: ['supabase-supervisor-shifts', { filters: filters }],
    queryFn: () => {
      if (!user_id) return Promise.resolve([]);

      return SupabaseSupervisorShiftService.getSupervisorShifts({ filters });
    },
    enabled: !!user_id
  });
};

export const useSupervisorShiftsByGroup = (group: string, start_date?: DateTime, end_date?: DateTime) => {
  return useQuery({
    queryKey: ['supabase-supervisor-shifts', { group, start_date, end_date }],
    queryFn: () => {
      if (!group) return Promise.resolve([]);

      const filters: SupervisorShiftFilter[] = [
        { field: 'group', operator: 'eq', value: group }
      ];

      if (start_date) {
        filters.push({ field: 'start_date_time', operator: 'gte', value: start_date.toISO() });
      }
      if (end_date) {
        filters.push({ field: 'start_date_time', operator: 'lte', value: end_date.toISO() });
      }

      return SupabaseSupervisorShiftService.getSupervisorShifts({ filters });
    },
    enabled: !!group
  });
};

export const useSupervisorShiftsForMonth = (month: DateTime, group?: string) => {
  return useQuery({
    queryKey: ['supabase-supervisor-shifts', { month: month.toISO(), group }],
    queryFn: () => SupabaseSupervisorShiftService.getSupervisorShiftsForMonth(month, group)
  });
};

export const useDeleteSupervisorShiftsByGroup = () => {
  return useMutation({
    mutationFn: ({ group, month }: { group: string; month?: DateTime }) =>
      SupabaseSupervisorShiftService.deleteSupervisorShiftsByGroup(group, month),
  });
};

export default SupabaseSupervisorShiftService;